/**
 * AutocompleteChips
 * 
 * @Author: Focci
 * @Date: 2023-09-27 12:22:07
 * @Last Modified by: Focci
 * @Last Modified time: 2023-09-27 12:22:07
 */

'use client'

import PropTypes from 'prop-types'
import clsx from 'clsx'
import useApp from '@hook/useApp'
import { map } from 'lodash-es'
import { ButtonBase } from '@comp/material'
import { useTranslations } from 'next-intl'
import { useCallback, useEffect, useRef } from 'react'
import { CloseableTag } from '../../Autocomplete'

export function AutocompleteChipsButton({ 
  label,
  className = 'border border-primary/50 bg-primary/10 text-primary',
  ...props
}) {
  return (
    <ButtonBase {...props}>
      <div 
        className={clsx([
          'flex items-center h-6 rounded-full text-f.7  px-2',
          className
        ])}
      >
        {label}
      </div>
    </ButtonBase>
  )
}

export default function AutocompleteChips({
  data = [],
  onChange = () => {},
  children
}) {
  const wrapper = useRef()
  const { locations } = useApp()
  const tc = useTranslations('common')

  const targetChips = map(data, (item) => ({
    ...item,
    label: locations?.[`${item.type}_${item.value}`]?.zh || item.label
  }))

  const handleDelete = useCallback((index) => {
    const tmp = [...targetChips]

    tmp.splice(index, 1)
    onChange(null, tmp)
  }, [targetChips, onChange])

  useEffect(() => {
    if(data && wrapper.current) {
      wrapper.current.scrollTop = 99999999
    }
  }, [data])

  if(targetChips.length === 0) {
    return null
  }
  
  return (
    <div
      ref={wrapper}
      className="
        flex flex-wrap gap-1 px-3 pt-3 max-h-[6rem] md:max-h-max
        overflow-y-auto md:overflow-visible
      "
    >
      {targetChips.map((item, index) => (
        <CloseableTag
          key={item.value}
          height="h-6"
          fontSize=".6rem"
          label={item.label} 
          onDelete={() => handleDelete(index)}
        />
      ))}
      {targetChips?.length > 1 && (
        <AutocompleteChipsButton 
          label={tc('clean')}
          onClick={() => onChange(null, [])}
        />
      )}
      {children}
    </div>
  )
}

AutocompleteChips.propTypes = {
  data: PropTypes.array,
  onChange: PropTypes.func,
  children: PropTypes.node,
}
